import { formatCell } from './columns';
import {
    GL_CODE_ABSENT,
    GL_WARNING_TOPICS,
    glGapsObservation,
    hasBlankGlCodeGap,
    type GlGapRow,
} from './membership-gl-gaps';

import type { FinalRecord } from './final-report';
import type { ModuleReportView } from './module-report-types';
import type { ClientSheet } from './report-sections';

/**
 * Membership programs' page in the final report. The record reads as the generic
 * label → value table (the migrator's columns, `glCode` among them); what this
 * page adds is the GL code: a chip on a program that has none, and one
 * observation that counts the gaps across clients (`membership-gl-gaps.ts`).
 *
 * It does not import `module-report.ts`, which imports it.
 */

/** The sheets' clients as the GL-gap reader takes them: one row per client, its records. */
export function sheetRows(sheets: readonly ClientSheet[]): GlGapRow[] {
    const rows = new Map<string, { row: ClientSheet['row']; records: FinalRecord[] }>();
    for (const sheet of sheets) {
        const id = sheet.row.tenant.tenantId;
        const entry = rows.get(id) ?? { row: sheet.row, records: [] };
        if (sheet.record !== null) entry.records.push(sheet.record);
        rows.set(id, entry);
    }
    return [...rows.values()].map(({ row, records }) => ({
        tenant: {
            tenantId: row.tenant.tenantId,
            slug: row.tenant.slug,
            displayName: row.tenant.name,
            orgId: row.tenant.orgId,
            isSandbox: row.tenant.isSandbox,
        },
        outcome: null,
        records,
        recordsTotal: row.recordsTotal,
    }));
}

export const MEMBERSHIP_PROGRAMS_REPORT: ModuleReportView = {
    recordNoun: 'membership program',
    table: (record, columns) => ({
        kind: 'values',
        rows: columns.map((column) => ({
            label: column.label,
            value: formatCell(record.columns[column.key]),
        })),
    }),
    preview: () => null,
    // The blank-GL programs the GL code observation counts: not the guest, not one
    // Veevart 2.0 does not take (`…_DELETED_IN_V2`, `…_INVALID_V2_ID`), and not a
    // record with no `glCode` column.
    recordChip: (record) => (hasBlankGlCodeGap(record) ? 'No GL code' : null),
    facts: () => [],
    observations: (sheets) => {
        const observation = glGapsObservation(sheetRows(sheets));
        return observation === null ? [] : [observation];
    },
    explainedWarningCodes: new Set([GL_CODE_ABSENT]),
    warningTopic: (code) => GL_WARNING_TOPICS[code] ?? null,
};
